// Graded slabs in Sell To Us (TKT-2026-107322).
//
// CANONICAL SOURCE: sortswift-buylist-api src/buylistEngine/gradedCardPricing.js
// GRADED_TIER_CONDITIONS and GRADED_COMPANIES below are copies of the lists the
// buylist pricing engine prices a slab by, and they must stay identical to it.
// There is no shared package between the two repos, so the per-repo copy is
// the house pattern, and buylistGraded.drift.test.js compares these against the
// engine's origin/main whenever that checkout is on disk. A tier or grader the
// engine does not know is one a customer could pick and never be offered, so
// the kiosk offers exactly these and nothing else (Mikah, 2026-10-02: "Only
// what gets an offer").
//
// A slab has no SKU of its own. It is the raw card's productId with a grade
// tier as its condition ('Grade 10') and the grader beside it ('PSA'). Only the
// top tier is priced per grader, but every tier is asked WITH its grader, the
// same question the buylist's submit guard re-asks of each slab line, and each
// tier and grader's figures are read only from the answer to its own question
// (the rule TKT-2026-106839 set for raw conditions).
//
// Whether a game shows Graded at all is not decided here. It is the buylist's
// own customer-facing flag, store-config gameConfigs[cat].gradedEnabled, which
// the kiosk backend passes through as gradedCategoryIds.

import { fetchBulkBuylistPrices } from '../api/products';

export const GRADED_TIER_CONDITIONS = ['Grade 7', 'Grade 8', 'Grade 9', 'Grade 9.5', 'Grade 10'];
export const GRADED_COMPANIES = ['PSA', 'BGS', 'CGC', 'SGC'];

export const DEFAULT_GRADER = 'PSA';
export const DEFAULT_TIER = 'Grade 10';

const norm = (v) => (v == null ? '' : String(v).trim().toUpperCase());
const tierKey = (tier) => norm(tier).replace(/\s+/g, '');
const answerKey = (tier, grader) => `${tierKey(tier)}|${norm(grader)}`;

/** The engine's key prefix for a tier: 'Grade 9.5' -> 'Grade9.5'. */
export function gradedKeyPrefix(tier) {
    return String(tier || '').replace(/\s+/g, '');
}

/**
 * True when the store's buylist buys slabs of this game: its categoryId is one
 * the kiosk backend read as gradedEnabled from store-config.
 */
export function isGradedCategory(gradedCategoryIds, categoryId) {
    if (!Array.isArray(gradedCategoryIds) || categoryId == null || categoryId === '') return false;
    const id = Number(categoryId);
    return Number.isFinite(id) && gradedCategoryIds.some((c) => Number(c) === id);
}

/** One bulk item per grader and tier, for one product, printing and language. */
export function gradedQuoteItems({ productId, printing, language }) {
    const items = [];
    for (const gradingCompany of GRADED_COMPANIES) {
        for (const condition of GRADED_TIER_CONDITIONS) {
            items.push({ productId, printing, condition, language, graded: true, gradingCompany });
        }
    }
    return items;
}

/**
 * Index the bulk answers by the tier and grader each was asked at.
 *
 * An answer is filed only under the grader the engine echoes, which it does on
 * every graded answer and on no other. An answer that carries no grader was
 * not priced as a slab (a do-not-buy rule, a darklisted name), so that tier
 * reads as not offered rather than borrowing another grader's figure.
 *
 * @returns {{ byTierGrader: Object }|null} null when nothing usable came back
 */
export function mergeGradedAnswers(prices, productId) {
    const byTierGrader = {};
    for (const entry of Array.isArray(prices) ? prices : []) {
        if (!entry || !entry.price || typeof entry.price !== 'object') continue;
        if (String(entry.productId) !== String(productId)) continue;
        const grader = norm(entry.gradingCompany || entry.price.gradingCompany);
        if (!grader) continue;
        const key = answerKey(entry.condition, grader);
        if (!byTierGrader[key]) byTierGrader[key] = entry.price;
    }
    return Object.keys(byTierGrader).length ? { byTierGrader } : null;
}

/**
 * What the store offers for one grade from one grader, from the answer asked
 * at exactly that grade and grader. { cash: 0, credit: 0 } when it offers nothing.
 */
export function gradedOffer(quotes, grader, tier) {
    const answer = quotes?.byTierGrader?.[answerKey(tier, grader)];
    if (!answer) return { cash: 0, credit: 0 };
    const prefix = gradedKeyPrefix(tier);
    return {
        cash: Number(answer[`${prefix}FinalCash`]) || 0,
        credit: Number(answer[`${prefix}FinalCredit`]) || 0,
    };
}

/** True when the store pays something for this grade from this grader. */
export function isGradedOffered(offer) {
    return (Number(offer?.cash) || 0) > 0 || (Number(offer?.credit) || 0) > 0;
}

/**
 * The payment legs this game's slabs can be paid on. The engine prices a slab
 * by the game's gradedPricingOptions, else its pricingOptions, so the switches
 * are read from the same block.
 */
export function gradedPaymentLegs(gameSettings, categoryId) {
    const game = (gameSettings && categoryId != null && gameSettings[categoryId]) || {};
    const options = game.gradedPricingOptions || game.pricingOptions || {};
    return {
        enableCash: options.enableCash !== false,
        enableCredit: options.enableCredit !== false,
    };
}

/**
 * The leg a slab line is booked on: the customer's choice when the store pays
 * it, else the other leg. Null when neither leg is offered. Mirrors the buylist
 * site's handleAddGradedToCart, so a slab is never booked on a $0 leg.
 */
export function resolveGradedPayment(paymentType, offer, legs = {}) {
    const cash = Number(offer?.cash) || 0;
    const credit = Number(offer?.credit) || 0;
    if (!(cash > 0) && !(credit > 0)) return null;
    const enableCash = legs.enableCash !== false;
    const enableCredit = legs.enableCredit !== false;
    let resolved = paymentType === 'Credit' ? 'Credit' : 'Cash';
    if (resolved === 'Cash' && (!(cash > 0) || !enableCash)) resolved = 'Credit';
    else if (resolved === 'Credit' && (!(credit > 0) || !enableCredit)) resolved = 'Cash';
    return resolved;
}

/** A cert number as the customer typed it, trimmed and capped. '' for none. */
export function cleanCertNumber(value) {
    return String(value == null ? '' : value).trim().slice(0, 40);
}

/** True for a cart line's variant that is a graded slab. */
export function isGradedVariant(variant) {
    return Boolean(variant && norm(variant.gradingCompany));
}

/** How a slab line reads in the cart and on the receipt: 'PSA Grade 10 / Normal'. */
export function gradedLineLabel(variant) {
    if (!isGradedVariant(variant)) return '';
    return `${norm(variant.gradingCompany)} ${variant.condAbbr || ''} / ${variant.printing || 'Normal'}`;
}

/**
 * Ask the store's buylist what it offers for this product as a slab, at every
 * grade from every grader, in one call. Resolves to the merged answers or null.
 */
export async function fetchGradedQuotes(storeName, { productId, printing, language }) {
    const response = await fetchBulkBuylistPrices(storeName, gradedQuoteItems({ productId, printing, language }));
    return mergeGradedAnswers(response?.data?.prices, productId);
}
